import * as React from "react";
import ProductSystems from "./ProductSystems/index";
import MiscFields from "./MiscFields/index";
import MaterialCategories from "./MaterialCategories/index";
import MaterialMeasurements from "./MaterialMeasurements/index";
import MaterialItems from "./MaterialItems/index";
import LaborMeasurements from "./LaborMeasurements/index";
import Contractors from "./Contractors/index";
import LaborItems from "./LaborItems/index";
import Tags from "./Tags/index";
import Suppliers from "./Suppliers";
import ContractorReportTypes from "./ContractorReportTypes/index";
import ContractorChangeOrders from "./ContractorChangeOrders/index";
import { Tab, Tabs, Box } from "@mui/material";
import { AppCSS } from "../../../components";
import { ColDiv, RowDiv } from "../../../components/misc/misc.styled";
import { TabsBox } from "./itemCreation.styled";
import { useAppSelector } from "../../../redux/hooks";
import { useAccess } from "../../../hooks/useAccess";
import AppLoader from "../../../components/loader";
import { logEvent, ITEM_CREATION_EVENTS } from "../../../utils/amplitude";

const ItemsCreationTabLabels = [
  "Product Systems",
  "Miscellaneous Fields",
  "Material Categories",
  "Material Measurements",
  "Materials",
  "Labor Measurements",
  "Contractors",
  "Labor",
  "Tags",
  "Suppliers",
  // "Contractor Reports",
  // "Contractor Change Orders",
];

export default function MaterialCreation() {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    logEvent(ITEM_CREATION_EVENTS.TAB_VIEWED, { 
      tab_name: ItemsCreationTabLabels[newValue], 
      tab_index: newValue 
    });
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  // Use the global hook for access permissions
  const { loading: accessLoading } = useAccess();

  // Fetch permissions from Redux using useAppSelector
  const permissions = useAppSelector((state) => state.userReducer.permissions);

  // Check if the user has write access for the "PRODUCT_SYSTEM" section
  const hasWriteAccess = permissions.some(
    (permission) => permission.name === "PRODUCT_SYSTEM" && permission.permission.includes("WRITE")
  );

  const renderTabPanel = (index: number) => {
    switch (index) {
      case 0:
        return <ProductSystems permissions={hasWriteAccess}/>;
      case 1:
        return <MiscFields permissions={hasWriteAccess}/>;
      case 2:
        return <MaterialCategories permissions={hasWriteAccess}/>;
      case 3:
        return <MaterialMeasurements permissions={hasWriteAccess}/>;
      case 4:
        return <MaterialItems permissions={hasWriteAccess}/>;
      case 5:
        return <LaborMeasurements permissions={hasWriteAccess}/>;
      case 6:
        return <Contractors permissions={hasWriteAccess}/>;
      case 7:
        return <LaborItems permissions={hasWriteAccess}/>;
      case 8:
        return <Tags permissions={hasWriteAccess} />;
      case 9:
        return <Suppliers permissions={hasWriteAccess} />;
      // case 10:
      //   return <ContractorReportTypes permissions={hasWriteAccess} />;
      // case 11:
      //   return <ContractorChangeOrders permissions={hasWriteAccess} />;
      default:
        return null;
    }
  };

  if (accessLoading) {
    return <AppLoader isLoading={accessLoading} />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        padding: { xs: "12px", md: "24px" },
      }}
    >
      <Box sx={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header Section */}
        <Box
          sx={{
            marginBottom: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              fontSize: { xs: "20px", md: "24px" },
              fontWeight: 600,
              color: "#1f2937",
              marginBottom: "8px",
            }}
          >
            Item Creation
          </Box>
          <Box
            sx={{
              fontSize: { xs: "13px", md: "14px" },
              color: "#6b7280",
            }}
          >
            Manage product systems, materials, labor, and more
          </Box>
        </Box>

        {/* Tabs and Content */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* Sidebar Navigation - Desktop */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              width: "280px",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                padding: "16px",
                position: "sticky",
                top: "24px",
                maxHeight: "calc(100vh - 48px)",
                overflowY: "auto",
              }}
            >
              <Tabs
                value={value}
                onChange={handleChange}
                orientation="vertical"
                TabIndicatorProps={{
                  style: {
                    display: "none",
                  },
                }}
                aria-label="item creation tabs"
                variant="scrollable"
                sx={{
                  "& .MuiTabs-flexContainer": {
                    gap: "4px",
                  },
                }}
              >
                {ItemsCreationTabLabels.map((label, index) => (
                  <Tab
                    key={index}
                    label={label}
                    {...a11yProps(index)}
                    sx={{
                      alignItems: "flex-start",
                      textAlign: "left",
                      padding: "12px 16px",
                      minHeight: "auto",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#4b5563",
                      borderRadius: "8px",
                      textTransform: "none",
                      transition: "all 0.2s",
                      "&:hover": {
                        backgroundColor: "#f3f4f6",
                      },
                      "&.Mui-selected": {
                        color: "white",
                        backgroundColor: "#4b87ed",
                      },
                    }}
                  />
                ))}
              </Tabs>
            </Box>
          </Box>

          {/* Mobile Tabs - Horizontal Scrollable */}
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              padding: "8px",
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons={false}
              TabIndicatorProps={{
                style: {
                  display: "none",
                },
              }}
              aria-label="item creation tabs"
              sx={{
                minHeight: "auto",
                "& .MuiTabs-flexContainer": {
                  gap: "8px",
                },
              }}
            >
              {ItemsCreationTabLabels.map((label, index) => (
                <Tab
                  key={index}
                  label={label}
                  {...a11yProps(index)}
                  sx={{
                    minHeight: "auto",
                    padding: "8px 16px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#4b5563",
                    borderRadius: "8px",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    minWidth: "auto",
                    border: value === index ? "none" : "1px solid #e5e7eb",
                    "&.Mui-selected": {
                      color: "white",
                      backgroundColor: "#4b87ed",
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              padding: 0,
              minHeight: { xs: "auto", md: "calc(100vh - 200px)" },
              maxHeight: { xs: "none", md: "calc(100vh - 200px)" },
              overflowY: "auto",
            }}
          >
            <Box sx={{ padding: { xs: "16px", md: "24px" } }}>
              {renderTabPanel(value)}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
